import { RecommendationKind } from "../App";
import { CardType } from "../components/Card";
import { debounce } from "ts-debounce";

export interface LoggingData {
	[data: string]: string | number;
}

type LoggingContext = "qna" | "cshp";

export async function doSessionLogging(props: LoggingData) {
	return new Promise(function(resolve, reject) {
		if (typeof C !== "undefined") {
			C.logger.doSessionLog(props, function() {
				resolve(props);
			});
			setTimeout(reject, 3000);
		} else {
			console.log("Logging", props);
			resolve(props);
		}
	});
}

export function logDropdownChange(data: {
	groupId: number;
	isbn13?: string;
	kind: RecommendationKind;
}) {
	const prefix = getPrefixByKind(data.kind);
	const logObj: LoggingData = {
		[`cshp_${prefix}_dropdown_click_group`]: data.groupId,
	};
	if (data.isbn13 && data.kind === RecommendationKind.TBS) {
		logObj[`cshp_${prefix}_dropdown_click`] = data.isbn13;
	}
	doSessionLogging(logObj);
}

export function logRecommendationAvailability(
	kind: RecommendationKind,
	context: LoggingContext = "cshp"
) {
	const prefix = getPrefixByKind(kind);
	doSessionLogging({
		[`${context}_${prefix}_available`]: 1,
	});
}

export async function doCardLogging(
	data: CardType,
	kind: RecommendationKind,
	recommendationId: number,
	context: LoggingContext = "cshp"
) {
	const logObj: LoggingData = {};
	const prefix = getPrefixByKind(kind);

	logObj[`${context}_${prefix}_click`] = data.id;
	logObj[`${context}_${prefix}_unique_id`] = recommendationId;
	logObj[`${context}_${prefix}_click_card_number`] = data.cardNumber;

	if (data.text) {
		logObj[`${context}_${prefix}_click_text`] = 1;
	}
	if (data.thumbnailURL) {
		logObj[`${context}_${prefix}_click_video`] = 1;
	} else if (data.imageURL) {
		logObj[`${context}_${prefix}_click_image`] = 1;
	}

	if (data.positiveReviewsCount) {
		logObj[`${context}_${prefix}_click_rating`] = 1;
	}

	return await doSessionLogging(logObj);
}

export function dropdownOpen(kind: RecommendationKind) {
	const prefix = getPrefixByKind(kind);
	doSessionLogging({ [`cshp_${prefix}_dropdown_open`]: 1 });
}

export function recommendationLoaded(
	data: {
		kind: RecommendationKind;
		recommendationId: number;
		groupid: number;
		isbn13?: string;
		itemsCount: number;
		categoriesCount?: number;
	},
	context: LoggingContext = "cshp"
) {
	const logObj: LoggingData = {};
	const prefix = getPrefixByKind(data.kind);

	logObj[`${context}_${prefix}_unique_id`] = data.recommendationId;
	logObj[`${context}_${prefix}_group_shown`] = data.groupid;
	data.categoriesCount &&
		(logObj[`${context}_${prefix}_dropdown_available`] = data.categoriesCount);
	logObj[`${context}_${prefix}_cards_shown`] = data.itemsCount;

	if (data.kind === RecommendationKind.TBS) {
		logObj[`cshp_${prefix}_isbn_shown`] = data.isbn13;
	}

	return doSessionLogging(logObj);
}

function getPrefixByKind(kind: RecommendationKind) {
	let prefix = "";
	if (kind === RecommendationKind.QnA) {
		prefix = "ns_rec_qna";
	} else {
		prefix = "lp_rec_tbs";
	}
	return prefix;
}

export async function QnACardClicked(data: CardType, recommendationId: number) {
	return await doCardLogging(
		data,
		RecommendationKind.QnA,
		recommendationId,
		"qna"
	);
}

export const ComponentViewLog = {
	componentViewLoggingDone: false,
	ref: null as HTMLDivElement,
	_debouncedLog: null as any,
	callback: null as any,
	threshold: 10,

	init(ref: HTMLDivElement, callback: any) {
		this.ref = ref;
		this._debouncedLog = debounce(this.log.bind(this), 200);
		this.callback = callback;
		if (typeof window !== "undefined") {
			window.addEventListener("scroll", this._debouncedLog);
			return this.log();
		}
		return false;
	},

	log() {
		if (!this.ref || typeof this.ref.offsetTop === "undefined") {
			return false;
		}
		const position = this.ref.getBoundingClientRect().top;
		const viewHeight = this.ref.clientHeight;
		const componentIsBelowView = window.innerHeight < position + this.threshold;
		const componentIsAboveView = position + viewHeight < this.threshold;
		const componentInView = !componentIsBelowView && !componentIsAboveView;
		if (componentInView) {
			this.componentViewLoggingDone = true;
			if (typeof this.callback === "function") {
				this.callback();
			}
			window.removeEventListener("scroll", this._debouncedLog);
			return true;
		}
		return false;
	},
};
